import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Plus, Pencil, Trash2, Search } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { CreateTenantDialog } from '../components/CreateTenantDialog';
import { EditTenantDialog } from '../components/EditTenantDialog';
import { ArchiveTenantDialog } from '../components/ArchiveTenantDialog';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/ui/status-badge';
import { TenantDto } from '../models';
import { getApiError, getTenants, updateTenantStatus } from '../services/api';
import { formatCount } from '../utils/formatCount';

const getStatus = (status: string) => {
  switch (status) {
    case 'Active': return { label: 'Активен', variant: 'success' as const };
    case 'Blocked': return { label: 'Заблокирован', variant: 'destructive' as const };
    case 'Terminated': return { label: 'Завершён', variant: 'default' as const };
    default: return { label: 'Тестовый', variant: 'warning' as const };
  }
};

const statusValue = (status: string) => status === 'Active' ? 2 : status === 'Blocked' ? 3 : status === 'Terminated' ? 4 : 1;

export const TenantsPage = () => {
  const [items, setItems] = useState<TenantDto[]>();
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTenantId, setEditingTenantId] = useState<number>();
  const [archivingTenant, setArchivingTenant] = useState<{ id: number; name: string }>();
  const [updatingTenantId, setUpdatingTenantId] = useState<number>();

  const load = useCallback(() => {
    setError('');
    getTenants().then((r) => setItems(r.data.data)).catch((e) => setError(getApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (tenantId: number, status: number) => {
    setUpdatingTenantId(tenantId);
    setError('');
    try {
      await updateTenantStatus(tenantId, status);
      load();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setUpdatingTenantId(undefined);
    }
  };

  const filtered = useMemo(() => (items || []).filter((item) => item.name.toLowerCase().includes(query.trim().toLowerCase()) || String(item.id).includes(query.trim())), [items, query]);
  const totals = useMemo(() => (items || []).reduce((acc, item) => ({ users: acc.users + item.userCount, terminals: acc.terminals + item.terminalCount, online: acc.online + item.onlineTerminalCount }), { users: 0, terminals: 0, online: 0 }), [items]);

  return (
    <AppLayout title="Организации">
      <PageHeader
        title="Медицинские организации"
        description="Отдельные tenant-контуры со своими личными кабинетами."
        action={<Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> Добавить организацию</Button>}
      />
      {error && <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-lg border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-muted px-2.5 py-1 font-mono text-xs text-foreground">{formatCount(items?.length || 0, ['организация', 'организации', 'организаций'])}</span>
            <span className="rounded-md bg-muted px-2.5 py-1 font-mono text-xs text-muted-foreground">{totals.users} пользователей</span>
            <span className="rounded-md bg-muted px-2.5 py-1 font-mono text-xs text-muted-foreground">{totals.online}/{totals.terminals} терминалов</span>
          </div>
          <div className="ml-auto flex w-full max-w-xs items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Найти организацию"
              className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {!items && !error ? <LoadingState /> : items && filtered.length === 0 ? (
          <EmptyState icon={<Building2 className="h-8 w-8" />} title={query ? 'Организации не найдены' : 'Организации пока не добавлены'} description={query ? 'Измените поисковый запрос.' : 'Создайте первую медицинскую организацию.'} />
        ) : items ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Организация</th>
                  <th className="px-4 py-3 font-medium">Администратор</th>
                  <th className="px-4 py-3 text-center font-medium">Компании</th>
                  <th className="px-4 py-3 text-center font-medium">Пользователи</th>
                  <th className="px-4 py-3 text-center font-medium">Терминалы</th>
                  <th className="px-4 py-3 font-medium">Статус</th>
                  <th className="px-4 py-3 text-right font-medium">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const statusInfo = getStatus(item.status);
                  return (
                    <tr key={item.id} className="border-b border-border/60 transition-colors last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-4 font-mono text-xs text-muted-foreground">#{item.id}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-xs font-semibold text-primary">
                            {item.name.slice(0, 2).toUpperCase()}
                          </div>
                          <p className="font-medium text-foreground">{item.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 font-mono text-xs text-muted-foreground">{item.administratorEmail || 'Не назначен'}</td>
                      <td className="px-4 py-4 text-center font-mono text-foreground">{item.companyCount}</td>
                      <td className="px-4 py-4 text-center font-mono text-foreground">{item.userCount}</td>
                      <td className="px-4 py-4 text-center font-mono">
                        <span className={item.onlineTerminalCount > 0 ? 'text-success' : 'text-muted-foreground'}>{item.onlineTerminalCount}</span>
                        <span className="text-muted-foreground">/{item.terminalCount}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <StatusBadge {...statusInfo} />
                          <select
                            value={statusValue(item.status)}
                            disabled={updatingTenantId === item.id}
                            onChange={(e) => changeStatus(item.id, Number(e.target.value))}
                            className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none"
                          >
                            <option value={1}>Тестовый</option>
                            <option value={2}>Активен</option>
                            <option value={3} disabled={item.id === 1}>Заблокирован</option>
                            <option value={4} disabled={item.id === 1}>Завершён</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditingTenantId(item.id)} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label={`Редактировать ${item.name}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button disabled={item.id === 1} onClick={() => setArchivingTenant({ id: item.id, name: item.name })} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive disabled:opacity-30" aria-label={`Архивировать ${item.name}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <CreateTenantDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(name) => { setCreateOpen(false); setSuccess(`Организация "${name}" создана`); load(); }} />
      <EditTenantDialog tenantId={editingTenantId} onClose={() => setEditingTenantId(undefined)} onSaved={(name) => { setEditingTenantId(undefined); setSuccess(`Организация "${name}" обновлена`); load(); }} />
      <ArchiveTenantDialog tenant={archivingTenant} onClose={() => setArchivingTenant(undefined)} onArchived={(name) => { setArchivingTenant(undefined); setSuccess(`Организация "${name}" архивирована`); load(); }} />
    </AppLayout>
  );
};
